import { PageWrapper } from '../components/layout/PageWrapper'
import { Card } from '../components/ui/Card'
import { useAppConfig } from '../hooks/useAppConfig'
import { Seo } from '../components/Seo'

export function HowItWorks() {
  const { platformFeeGbp, paywallEnabled } = useAppConfig()
  const fee = `£${platformFeeGbp}`

  const faqs = paywallEnabled ? [
    {
      q: 'How much does it cost?',
      a: `We charge a ${fee} platform fee, taken upfront before we start the search. That's it from us. Your plumber's callout fee is agreed directly with them and paid on arrival - we never handle that money.`,
    },
    {
      q: `When is the ${fee} refunded?`,
      a: 'Automatically, in three situations: (1) we can\'t find an available plumber near you; (2) you cancel after seeing the callout fees quoted by plumbers; or (3) no plumber confirms availability for your request.',
    },
    {
      q: 'Are the plumbers qualified?',
      a: 'We only list plumbers who meet our qualification standards. Profiles are populated using third-party data from industry directories and review platforms, and we cross-reference publicly available certification and registration records. We do not independently inspect every plumber\'s work, but we filter out unrated or poorly rated tradespeople.',
    },
    {
      q: 'How are plumbers found?',
      a: 'We contact qualified plumbers near your postcode, checking availability, callout fee, and estimated arrival time - usually within a couple of minutes.',
    },
    {
      q: 'What if no plumbers are available?',
      a: `We'll let you know immediately and issue a full refund of the ${fee} fee. You can try again in 15 minutes - availability changes quickly.`,
    },
    {
      q: "What if the plumber doesn't show up?",
      a: `Contact us and we'll investigate. If the plumber fails to attend without good reason, we'll issue a full refund of the ${fee} fee or work to find you an alternative.`,
    },
    {
      q: 'Is FixDelivered available 24/7?',
      a: 'Yes. We match plumbers around the clock. Individual plumber availability varies, but we always try to find someone within the hour.',
    },
  ] : [
    {
      q: 'How much does it cost?',
      a: "Nothing. Right now FixDelivered is completely free to use, start to finish - no platform fee, upfront or otherwise. We start contacting plumbers as soon as you submit your request, and confirming your booking costs nothing too. Your plumber's callout fee is agreed directly with them and paid on arrival - we never handle that money.",
    },
    {
      q: 'Do I need to pay anything, or get a refund?',
      a: "There's nothing to pay us and nothing to refund, because we never take a platform fee in the first place right now. The only payment involved anywhere is the plumber's own callout fee, which you agree directly with them and pay on arrival.",
    },
    {
      q: 'Are the plumbers qualified?',
      a: 'We only list plumbers who meet our qualification standards. Profiles are populated using third-party data from industry directories and review platforms, and we cross-reference publicly available certification and registration records. We do not independently inspect every plumber\'s work, but we filter out unrated or poorly rated tradespeople.',
    },
    {
      q: 'How are plumbers found?',
      a: 'We contact qualified plumbers near your postcode, checking availability, callout fee, and estimated arrival time - usually within a couple of minutes.',
    },
    {
      q: 'What if no plumbers are available?',
      a: "We'll let you know immediately. Since we never charge a platform fee right now, there's nothing to refund - you can just try again in 15 minutes, availability changes quickly.",
    },
    {
      q: "What if the plumber doesn't show up?",
      a: "Contact us and we'll investigate. If the plumber fails to attend without good reason, we'll work to find you an alternative straight away.",
    },
    {
      q: 'Is FixDelivered available 24/7?',
      a: 'Yes. We match plumbers around the clock. Individual plumber availability varies, but we always try to find someone within the hour.',
    },
  ]

  return (
    <PageWrapper>
      <Seo
        title="How It Works — Emergency Plumber in 1 Hour"
        description={paywallEnabled
          ? `How FixDelivered works: tell us your emergency, we contact qualified local plumbers, and confirm one — typically within the hour. ${fee} flat booking fee, fully refundable if we can't deliver.`
          : "How FixDelivered works: tell us your emergency, we contact qualified local plumbers immediately, and confirm one — typically within the hour. Completely free right now, no booking fee."}
        path="/how-it-works"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        }}
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">How FixDelivered works</h1>
        <p className="text-gray-500 mb-10">Getting an emergency plumber has never been simpler.</p>

        <div className="space-y-6 mb-14">
          {[
            {
              step: 1,
              title: 'Tell us the problem',
              body: paywallEnabled
                ? `Fill in our quick form: describe the issue, add your postcode, then enter your contact details. We charge our ${fee} fee at this point - before the search starts.`
                : 'Fill in our quick form: describe the issue, add your postcode, then enter your contact details. We start contacting plumbers immediately - completely free, no charge at any point.',
            },
            {
              step: 2,
              title: 'We contact local plumbers',
              body: 'We contact qualified plumbers near your postcode, checking availability, callout fee, and estimated arrival time.',
            },
            {
              step: 3,
              title: 'We confirm the fastest match',
              body: paywallEnabled
                ? "Once we have responses, we rank plumbers by arrival time and confirm with the best available. You'll see their name, callout fee, and arrival time before they're dispatched."
                : "Once we have responses, you'll see each plumber's name, callout fee, and arrival time. Pick one and confirm the booking - free, no platform fee to pay.",
            },
            {
              step: 4,
              title: 'Plumber on the way',
              body: "Your plumber heads straight to you. You'll get an SMS with their name and number. Pay their callout fee directly on arrival - we never handle that money.",
            },
          ].map(({ step, title, body }) => (
            <Card key={step} className="flex gap-5">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-900 text-white rounded-full flex items-center justify-center font-bold text-sm border-2 border-gold-500">
                {step}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
                <p className="text-gray-600 text-sm">{body}</p>
              </div>
            </Card>
          ))}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently asked questions</h2>
        <div className="space-y-4">
          {faqs.map(({ q, a }) => (
            <Card key={q}>
              <h3 className="font-semibold text-gray-900 mb-2">{q}</h3>
              <p className="text-sm text-gray-600">{a}</p>
            </Card>
          ))}
        </div>
      </div>
    </PageWrapper>
  )
}
